import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { InvitationManagementResponse } from '../../../types/invitations'

const getSupabaseUrl = () => {
  const url = process.env.REACT_APP_SUPABASE_URL
  if (!url) {
    throw new Error('REACT_APP_SUPABASE_URL environment variable is not configured')
  }
  return url
}

const InviteAccept: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const loadInvitation = useCallback(async () => {
    try {
      if (!user) {
        // Redirect to sign in with return path
        navigate(`/auth/signin?redirect=/invite/${token}`)
        return
      }

      // Get invitation details (we'll need to fetch this from the backend)
      // For now, we'll proceed with the accept/decline actions
      setLoading(false)
    } catch (err: any) {
      setError(err.message || 'Failed to load invitation')
      setLoading(false)
    }
  }, [user, token, navigate])

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link')
      setLoading(false)
      return
    }

    loadInvitation()
  }, [loadInvitation, token])

  const handleAccept = async () => {
    if (!user || !token) return

    setLoading(true)
    setError('')

    try {
      const { data: authData } = await supabase.auth.getSession()
      const authToken = authData.session?.access_token

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/manage-invitations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          action: 'accept',
          token
        })
      })

      const data: InvitationManagementResponse = await response.json()
      
      if (data.success) {
        setSuccess('Invitation accepted successfully! You now have access to the collection.')
        setTimeout(() => {
          navigate('/')
        }, 3000)
      } else {
        setError(data.error || 'Failed to accept invitation')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to accept invitation')
    } finally {
      setLoading(false)
    }
  }

  const handleDecline = async () => {
    if (!user || !token) return

    setLoading(true)
    setError('')

    try {
      const { data: authData } = await supabase.auth.getSession()
      const authToken = authData.session?.access_token

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/manage-invitations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          action: 'decline',
          token
        })
      })

      const data: InvitationManagementResponse = await response.json()
      
      if (data.success) {
        setSuccess('Invitation declined.')
        setTimeout(() => {
          navigate('/')
        }, 2000)
      } else {
        setError(data.error || 'Failed to decline invitation')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to decline invitation')
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-isabelline">
        <div className="text-center">
          <div className="text-4xl mb-4">🍪</div>
          <div className="text-gray-600">Loading invitation...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-isabelline flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <div className="text-center mb-6">
            <div className="text-4xl mb-4">🍪</div>
            <h1 className="text-2xl font-bold text-battleship-gray">Collection Invitation</h1>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
              {success}
            </div>
          )}

          {!success && !error && (
            <>
              <div className="text-center mb-6">
                <p className="text-gray-600">
                  You've been invited to access a cookie cutter collection! 
                  Would you like to accept this invitation?
                </p>
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={handleDecline}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  disabled={loading}
                >
                  Decline
                </button>
                <button
                  onClick={handleAccept}
                  className="flex-1 px-4 py-2 bg-taupe text-white rounded-md hover:bg-battleship-gray"
                  disabled={loading}
                >
                  Accept Invitation
                </button>
              </div>
            </>
          )}

          {success && (
            <div className="text-center">
              <p className="text-gray-600">Redirecting you to the dashboard...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default InviteAccept